import * as path from "node:path";
import * as fs from "node:fs";
import type { TokenCredential } from "@azure/identity";
import {
  rotateConnectionAuth,
} from "@m8t-stack/github-app-auth";
import { serializeBrainLink, type BrainLink } from "@m8t-stack/api-contract";
import { LocalCliError } from "./errors.js";
import { readAgentYaml, patchAgentYaml, writeAgentYaml, type BrainBlock } from "./agent-yaml.js";
import { renderPersonaBody } from "./persona-render.js";
import { appendBrainLoader } from "./brain-loader-block.js";
import { hasA2aSnippet, appendSnippet as appendA2aSnippet } from "./a2a-snippet.js";
import { getAgentVersion, type AgentDefinition } from "./foundry-agent-get.js";
import { mirrorBrainYaml } from "./brain-yaml-mirror.js";
import type { FoundryProject } from "./foundry-project.js";

const FOUNDRY_ARM_API = "2025-04-01-preview";
const FOUNDRY_DATA_SCOPE = "https://ai.azure.com/.default";
const ARM_SCOPE = "https://management.azure.com/.default";
const MCP_TOOL_NAMES = [
  "get_file_contents",
  "create_or_update_file",
  "push_files",
  "search_code",
  "search_repositories",
];
const MCP_SERVER_URL = "https://api.githubcopilot.com/mcp";

export interface LinkBrainArgs {
  credential: TokenCredential;
  kvUri: string;
  projectEndpoint: string;       // data-plane URL
  projectArmId: string;          // /subscriptions/.../projects/<proj>
  agentName: string;
  repo: string;                  // "owner/name"
  branch: string;
  repoRoot: string;              // path to m8t-stack repo (for brain-loader.md)
  home?: string;                 // for testing — defaults to os.homedir()
  installationId?: string;       // pre-known id (e.g. from CLI poll); when absent, caller polls
  skipIfLinked?: boolean;        // if true, skip createVersion + brain.yaml write when already linked
  onProgress?: (msg: string) => void;
  // Hosted-agent fields — required when the agent has kind:"hosted"
  subscriptionId?: string;
  project?: FoundryProject;
  modelDeployment?: string;
  allowNonReasoning?: boolean;
  personaPathOverride?: string;  // --persona: re-render from this file instead of the yaml's personaPath
}

export interface LinkBrainResult {
  installationId: string;
  connectionName: string;
  foundryVersion?: string;
  alreadyLinked: boolean;
}

/**
 * The convergent link step (DESIGN §7.2 steps 3–8).
 *
 * Caller (CLI command) has already:
 *   - Verified agent exists (or created the repo, for `m8t brain create`)
 *   - Done the App-install poll (or expects to via this function's
 *     installationId arg if pre-known)
 *   - Run the inline `checkAppHealth` precondition
 *
 * This function:
 *   1. Reads current agent definition (to clone instructions + tools)
 *   2. Reads per-agent yaml for personaPath + fillableFieldValues
 *   3. Re-renders persona body + appends brain loader (templated with {{brain_repo}})
 *   4. Mints installation token + PATCHes Foundry connection
 *      (rotateConnectionAuth — creates connection on first run; see Step 7
 *      for the create-if-missing detail)
 *   5. Composes + POSTs createVersion with brain tool + metadata.brain
 *   6. Writes .m8t/brain.yaml mirror to the repo
 *   7. Patches local per-agent yaml with the brain block
 *
 * Idempotent: when `skipIfLinked=true` AND metadata.brain already matches
 * args, steps 5–7 are skipped but token rotation (step 4) still runs.
 */
export async function linkBrain(args: LinkBrainArgs): Promise<LinkBrainResult> {
  // eslint-disable-next-line @typescript-eslint/no-empty-function -- noop progress stub
  const progress = args.onProgress ?? ((_m: string) => {});
  const connectionName = `brain-${args.agentName}`;

  if (!args.installationId) {
    throw new LocalCliError({
      code: "LINK_NO_INSTALLATION_ID",
      message: "linkBrain requires installationId. CLI command must poll for it before calling.",
    });
  }

  progress("Reading current agent definition…");
  const current = await getAgentVersion({
    credential: args.credential,
    projectEndpoint: args.projectEndpoint,
    agentName: args.agentName,
  });

  // Kind-aware: hosted agents bypass the MCP attachment + Foundry connection
  // entirely — the container self-mints and reads/writes via the GitHub API.
  if (current.definition.kind === "hosted") {
    if (!args.project || !args.subscriptionId) {
      throw new LocalCliError({
        code: "HOSTED_LINK_MISSING_CONTEXT",
        message: "linkBrain: hosted agents require project + subscriptionId (the CLI command must resolve them).",
      });
    }
    progress("hosted agent — enabling in-container brain access…");
    const { enableHostedBrain } = await import("./enable-hosted-brain.js");
    const res = await enableHostedBrain({
      credential: args.credential,
      subscriptionId: args.subscriptionId,
      project: args.project,
      kvUri: args.kvUri,
      agentName: args.agentName,
      repo: args.repo,
      branch: args.branch,
      installationId: args.installationId,
      modelDeployment: args.modelDeployment,
      allowNonReasoning: args.allowNonReasoning,
      onProgress: args.onProgress,
    });
    return {
      installationId: args.installationId,
      connectionName: "in-container",
      foundryVersion: res.version,
      alreadyLinked: res.alreadyLinked,
    };
  }

  // Idempotency check: skip the heavy steps if metadata.brain already matches.
  const currentLink = parseExistingBrain(current.metadata?.brain);
  const alreadyLinked = !!(
    args.skipIfLinked &&
    currentLink?.repo === args.repo &&
    currentLink.installationId === args.installationId &&
    currentLink.credentialRef === connectionName
  );

  progress("Rotating App installation token in Foundry connection…");
  await ensureConnectionExists({
    credential: args.credential,
    projectArmId: args.projectArmId,
    connectionName,
  });
  const rotation = await rotateConnectionAuth({
    credential: args.credential,
    kvUri: args.kvUri,
    projectArmId: args.projectArmId,
    connectionName,
    installationId: args.installationId,
    repository: args.repo,
  });
  progress(`Token rotated (expires ${rotation.expiresAt.toISOString()})`);

  let foundryVersion: string | undefined;

  if (!alreadyLinked) {
    const loaderPath = path.join(args.repoRoot, "targets/foundry/brain-loader.md");
    const loaderTemplate = fs.readFileSync(loaderPath, "utf8");
    const loader = loaderTemplate.replaceAll("{{brain_repo}}", args.repo);

    const yaml = readAgentYaml(args.agentName, args.home);
    const personaPath = args.personaPathOverride ?? yaml?.personaPath;

    let base: string;
    if (personaPath) {
      progress("Re-rendering persona body with brain loader…");
      const resolved = personaPath.startsWith("/") ? personaPath : path.join(args.repoRoot, personaPath);
      base = renderPersonaBody(resolved, yaml?.fillableFieldValues ?? {}, args.agentName);
    } else {
      // Fallback (no local yaml — e.g. a REST/SDK-created agent): base on the
      // deployed instructions; appendBrainLoader strips any prior loader first.
      progress("No local persona yaml — basing instructions on the deployed agent…");
      base = current.definition.instructions ?? "";
    }
    let instructionsWithLoader = appendBrainLoader(base, loader);
    // Relink-safety: the persona re-render above discards the live instructions,
    // which carry the a2a delegate snippet (enableA2a appends it post-deploy, not
    // in the persona file). Re-attach it when the deployed agent already had it so
    // --force never silently strips the "Working with other workers" guidance. The
    // a2a *tool* is preserved separately (cloned in createBrainEnabledVersion).
    // appendA2aSnippet strips-then-appends, so this is idempotent and safe even in
    // the fallback branch where `base` already inlines the snippet (no duplication).
    if (hasA2aSnippet(current.definition.instructions ?? "")) {
      instructionsWithLoader = appendA2aSnippet(instructionsWithLoader);
    }

    const link: BrainLink = {
      repo: args.repo,
      branch: args.branch,
      topology: "per-worker",
      schemaVersion: "1",
      credentialRef: connectionName,
      installationId: args.installationId,
    };

    progress("Deploying brain-enabled agent version…");
    foundryVersion = await createBrainEnabledVersion({
      credential: args.credential,
      projectEndpoint: args.projectEndpoint,
      agentName: args.agentName,
      currentDefinition: current.definition,
      currentMetadata: current.metadata ?? {},
      instructionsWithLoader,
      connectionName,
      brainLinkJson: serializeBrainLink(link),
    });

    progress("Mirroring .m8t/brain.yaml to brain repo…");
    await mirrorBrainYaml({
      credential: args.credential,
      kvUri: args.kvUri,
      installationId: args.installationId,
      repo: args.repo,
      branch: args.branch,
      persona: yaml?.personaVersion
        ? `${current.metadata?.persona ?? args.agentName}@${yaml.personaVersion}`
        : current.metadata?.persona ?? args.agentName,
      agent: args.agentName,
    });

    progress("Recording the brain link in the per-agent yaml…");
    const brainBlock: BrainBlock = {
      repo: args.repo,
      branch: args.branch,
      installationId: args.installationId,
      credentialRef: connectionName,
      linkedAt: new Date().toISOString(),
    };
    if (yaml) {
      patchAgentYaml(args.agentName, { brain: brainBlock }, args.home);
    } else {
      // Synthesize a minimal yaml so brain unlink / agent remove / future ops work.
      writeAgentYaml(
        args.agentName,
        {
          agentName: args.agentName,
          agentId: "",
          projectEndpoint: args.projectEndpoint,
          // model may be empty for an SDK-created agent without a deployed model field
          model: typeof current.definition.model === "string" ? current.definition.model : "",
          personaVersion: current.metadata?.personaVersion ?? null,
          personaPath: "",
          deployedAt: new Date().toISOString(),
          playgroundUrl: "",
          environments: { default: { projectEndpoint: args.projectEndpoint } },
          brain: brainBlock,
        },
        args.home,
      );
    }
  }

  return {
    installationId: args.installationId,
    connectionName,
    foundryVersion,
    alreadyLinked,
  };
}

// ──── helpers ───────────────────────────────────────────────────────────────

function parseExistingBrain(raw: string | undefined): BrainLink | undefined {
  if (!raw) return undefined;
  try { return JSON.parse(raw) as BrainLink; } catch { return undefined; }
}

async function ensureConnectionExists(args: {
  credential: TokenCredential;
  projectArmId: string;
  connectionName: string;
}): Promise<void> {
  const armToken = await args.credential.getToken(ARM_SCOPE);
  if (!armToken?.token) {
    throw new LocalCliError({ code: "ARM_AUTH", message: "Could not acquire ARM token" });
  }
  const url = `https://management.azure.com${args.projectArmId}/connections/${args.connectionName}?api-version=${FOUNDRY_ARM_API}`;
  // GET first — exists?
  const getRes = await fetch(url, { headers: { Authorization: `Bearer ${armToken.token}` } });
  if (getRes.ok) return;
  if (getRes.status !== 404) {
    const text = await getRes.text();
    throw new LocalCliError({
      code: "CONN_GET_FAILED",
      message: `GET ${url}: HTTP ${getRes.status.toString()}\n${text.slice(0, 300)}`,
    });
  }
  // PUT (create with placeholder credentials; rotateConnectionAuth will PATCH the real token)
  const body = JSON.stringify({
    properties: {
      authType: "CustomKeys", category: "CustomKeys",
      target: MCP_SERVER_URL,
      isSharedToAll: false,
      credentials: { keys: { Authorization: "Bearer placeholder-will-be-rotated" } },
      metadata: { managedBy: "m8t-brain-f02" },
    },
  });
  const putRes = await fetch(url, {
    method: "PUT",
    headers: { Authorization: `Bearer ${armToken.token}`, "Content-Type": "application/json" },
    body,
  });
  if (!putRes.ok) {
    const text = await putRes.text();
    throw new LocalCliError({
      code: "CONN_CREATE_FAILED",
      message: `PUT ${url}: HTTP ${putRes.status.toString()}\n${text.slice(0, 300)}`,
    });
  }
}

async function createBrainEnabledVersion(args: {
  credential: TokenCredential;
  projectEndpoint: string;
  agentName: string;
  currentDefinition: AgentDefinition;
  currentMetadata: Record<string, string>;
  instructionsWithLoader: string;
  connectionName: string;
  brainLinkJson: string;
}): Promise<string> {
  const fndToken = await args.credential.getToken(FOUNDRY_DATA_SCOPE);
  if (!fndToken?.token) {
    throw new LocalCliError({ code: "FOUNDRY_AUTH", message: "Could not acquire Foundry data-plane token" });
  }
  // Strip any existing brain tool, then append the canonical one.
  const otherTools = (args.currentDefinition.tools ?? []).filter(
    (t) => !(t.type === "mcp" && (t as { server_label?: string }).server_label === "brain"),
  );
  const brainTool = {
    type: "mcp" as const,
    server_label: "brain",
    server_url: MCP_SERVER_URL,
    allowed_tools: MCP_TOOL_NAMES,
    require_approval: "never" as const,
    project_connection_id: args.connectionName,
  };
  const definition: AgentDefinition = {
    ...args.currentDefinition,
    instructions: args.instructionsWithLoader,
    tools: [...otherTools, brainTool],
  };
  const metadata: Record<string, string> = {
    ...args.currentMetadata,
    brain: args.brainLinkJson,
  };
  const url = `${args.projectEndpoint}/agents/${args.agentName}/versions?api-version=v1`;
  const res = await fetch(url, {
    method: "POST",
    headers: { Authorization: `Bearer ${fndToken.token}`, "Content-Type": "application/json" },
    body: JSON.stringify({ definition, metadata }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new LocalCliError({
      code: "AGENT_CREATE_VERSION_FAILED",
      message: `POST ${url}: HTTP ${res.status.toString()}\n${text.slice(0, 500)}`,
    });
  }
  const data = (await res.json()) as { version?: string };
  if (!data.version) {
    throw new LocalCliError({ code: "AGENT_CREATE_VERSION_NO_VERSION", message: `createVersion returned no version` });
  }
  return data.version;
}

